/* eslint-disable no-unused-vars */
// @flow
import React, { useMemo } from 'react';
import { useHistory } from 'react-router';
import { useSelector, useDispatch } from 'react-redux';

import actionTypes from '../../store/profile/actionTypes';
import Button from '../../components/Button';
import routes from '../../constants/routes';
import AdvancedBG from '../../assets/icons/price/advanced.png';

import { PRICE_KEY, RATE_NAME } from '../../constants/payments';

import styles from './styles.module.scss';

type Props = {
  isYear: boolean,
  setModalInfo: ({
    title: string,
    id: string,
  }) => void,
  cancelHandler: () => void,
  resumeHandler: () => void,
  user: {
    ban: boolean,
    email: string,
    id: number,
    payment_date: string,
    payment_method: string,
    payment_status: string,
    rate: string,
  },
}

const usePricingData = ({
  isYear, user, setModalInfo, cancelHandler, resumeHandler,
}: Props) => {
  const history = useHistory();
  const dispatch = useDispatch();
  const isLoggedIn = useSelector((state) => state.auth.isAuthenticated);

  const isBasicActive = user?.rate === RATE_NAME.BASIC && user?.payment_status === 'active';
  const isBasicAnnualActive = user?.rate === RATE_NAME.BASIC_YEAR && user?.payment_status === 'active';

  const isAdvancedActive = user?.rate === RATE_NAME.ADVANCED && user?.payment_status === 'active';
  const isAdvancedAnnualActive = user?.rate === RATE_NAME.ADVANCED_YEAR && user?.payment_status === 'active';

  const isPlo4Active = user?.rate === RATE_NAME.PLO4 && user?.payment_status === 'active';
  const isPlo5Active = user?.rate === RATE_NAME.PLO5 && user?.payment_status === 'active';
  const isPlo4Plo5Active = user?.rate === RATE_NAME.PLO4PLO5 && user?.payment_status === 'active';
  const isPlo4YearActive = user?.rate === RATE_NAME.PLO4_YEAR && user?.payment_status === 'active';
  const isPlo5YearActive = user?.rate === RATE_NAME.PLO5_YEAR && user?.payment_status === 'active';
  const isPlo4Plo5YearActive = user?.rate === RATE_NAME.PLO4PLO5_YEAR && user?.payment_status === 'active';

  const isPlo4 = user?.rate === RATE_NAME.PLO4;
  const isPlo5 = user?.rate === RATE_NAME.PLO5;
  const isPlo4Plo5 = user?.rate === RATE_NAME.PLO4PLO5;
  const isPlo4Year = user?.rate === RATE_NAME.PLO4_YEAR;
  const isPlo5Year = user?.rate === RATE_NAME.PLO5_YEAR;
  const isPlo4Plo5Year = user?.rate === RATE_NAME.PLO4PLO5_YEAR;

  const isTest = user?.rate === RATE_NAME.ADVANCED_TRIAL && user?.payment_status === 'active';

  const isStripe = user?.payment_method === 'Stripe';
  const isPaypal = user?.payment_method === 'Paypal';
  const isActiveSub = user?.payment_status === 'active';
  const isPausedSub = user?.payment_status === 'paused';
  const isPendingSub = user?.payment_status === 'pending';

  const isDev = window.location.host.includes('develop') || window.location.host.includes('localhost');
  const unsubscribeHref = isDev ? 'https://www.sandbox.paypal.com/cgi-bin/webscr?cmd=_subscr-find&alias=67GJRXM2ZJYFJ' : 'https://www.paypal.com/cgi-bin/webscr?cmd=_subscr-find&alias=RKLD69HRH8X9J';

  const handlerButtonSubscribe = (url: string) => {
    const route = isLoggedIn ? url : routes.auth.root;

    history.push(route);
  };

  const handleButton = (onClick: (any) => void, name: ?string = 'Subscribe now', bgColor: ?string = '#FFFFF') => (
    <Button
      className={styles.button}
      customBackground={bgColor}
      onClick={onClick}
      name={name}
    />
  );

  const handlePaypalButton = (isBasic: boolean, bgColor: ?string) => (
    <Button
      className={styles.button}
      customBackground={bgColor}
      onClick={() => { window.location.href = unsubscribeHref; }}
      name="Cancel current subscription"
    />
  );

  // eslint-disable-next-line no-unused-vars
  const buttonTrial = (
    <Button
      className={styles.button}
      onClick={() => {
        handlerButtonSubscribe(routes.app.payments.toPayments(RATE_NAME.ADVANCED_TRIAL));
      }}
      name="Buy now"
    />
  );

  const handlerCancelSub = () => {
    cancelHandler();
    // dispatch({ type: actionTypes.CANCEL_SUB, payload: true });
  };

  const handlerResumeButton = () => {
    resumeHandler();
  };

  const buttonUpgrade = (info: {
    title: string,
    id: string,
    to: string,
  }, bgColor) => (
    <Button
      className={styles.button}
      customBackground={bgColor}
      onClick={() => {
        setModalInfo(info);
        dispatch({ type: actionTypes.UPGRADE_COST, payload: info.id });
      }}
      name="Upgrade"
    />
  );

  const disabledButton = (bgColor, text = 'Disabled') => (
    <Button
      className={styles.button}
      customBackground={bgColor}
      disabled
      name={text}
    />
  );

  const handleButtonType = (button: HTMLButtonElement, type: string, bgColor: ?string = '#FFFFF') => {
    if (isActiveSub && isPaypal) {
      return handlePaypalButton(isBasicActive || isBasicAnnualActive, bgColor);
    }

    // eslint-disable-next-line max-len
    if ((isBasicActive || isAdvancedActive || isBasicAnnualActive || isAdvancedAnnualActive) && isStripe) {
      if (type === RATE_NAME.PLO4) return buttonUpgrade({ title: 'Upgrade subscription', id: PRICE_KEY.PLO4, to: 'PLO4 (€45/month)' }, bgColor);
      if (type === RATE_NAME.PLO5) return buttonUpgrade({ title: 'Upgrade subscription', id: PRICE_KEY.PLO5, to: 'PLO5 (€45/month)' }, bgColor);
      if (type === RATE_NAME.PLO4PLO5) return buttonUpgrade({ title: 'Upgrade subscription', id: PRICE_KEY.PLO4PLO5, to: 'PLO4+PLO5 (€70/month)' }, bgColor);
      if (type === RATE_NAME.PLO4_YEAR) return buttonUpgrade({ title: 'Upgrade subscription', id: PRICE_KEY.PLO4_YEAR, to: 'PLO4 (€405/year)' }, bgColor);
      if (type === RATE_NAME.PLO5_YEAR) return buttonUpgrade({ title: 'Upgrade subscription', id: PRICE_KEY.PLO5_YEAR, to: 'PLO5 (€405/year)' }, bgColor);
      if (type === RATE_NAME.PLO4PLO5_YEAR) return buttonUpgrade({ title: 'Upgrade subscription', id: PRICE_KEY.PLO4PLO5_YEAR, to: 'PLO4+PLO5 (€660/year)' }, bgColor);
    }

    if (isPlo4Active && isStripe) {
      if (type === RATE_NAME.PLO4) return handleButton(handlerCancelSub, 'Cancel PLO4', bgColor);
      if (type === RATE_NAME.PLO5) return handleButton(handlerCancelSub, 'Cancel PLO4', bgColor);
      if (type === RATE_NAME.PLO4PLO5) return buttonUpgrade({ title: 'Upgrade subscription', id: PRICE_KEY.PLO4PLO5, to: 'PLO4+PLO5 (€70/month)' }, bgColor);
      if (type === RATE_NAME.PLO4_YEAR) return buttonUpgrade({ title: 'Upgrade subscription', id: PRICE_KEY.PLO4_YEAR, to: 'PLO4 (€405/year)' }, bgColor);
      if (type === RATE_NAME.PLO5_YEAR) return buttonUpgrade({ title: 'Upgrade subscription', id: PRICE_KEY.PLO5_YEAR, to: 'PLO5 (€405/year)' }, bgColor);
      if (type === RATE_NAME.PLO4PLO5_YEAR) return buttonUpgrade({ title: 'Upgrade subscription', id: PRICE_KEY.PLO4PLO5_YEAR, to: 'PLO4+PLO5 (€660/year)' }, bgColor);
    }

    if (isPlo5Active && isStripe) {
      if (type === RATE_NAME.PLO4) return handleButton(handlerCancelSub, 'Cancel PLO4', bgColor);
      if (type === RATE_NAME.PLO5) return handleButton(handlerCancelSub, 'Cancel PLO4', bgColor);
      if (type === RATE_NAME.PLO4PLO5) return buttonUpgrade({ title: 'Upgrade subscription', id: PRICE_KEY.PLO4PLO5, to: 'PLO4+PLO5 (€70/month)' }, bgColor);
      if (type === RATE_NAME.PLO4_YEAR) return buttonUpgrade({ title: 'Upgrade subscription', id: PRICE_KEY.PLO4_YEAR, to: 'PLO4 (€405/year)' }, bgColor);
      if (type === RATE_NAME.PLO5_YEAR) return buttonUpgrade({ title: 'Upgrade subscription', id: PRICE_KEY.PLO5_YEAR, to: 'PLO5 (€405/year)' }, bgColor);
      if (type === RATE_NAME.PLO4PLO5_YEAR) return buttonUpgrade({ title: 'Upgrade subscription', id: PRICE_KEY.PLO4PLO5_YEAR, to: 'PLO4+PLO5 (€660/year)' }, bgColor);
    }

    if (isPlo4Plo5Active && isStripe) {
      if (type === RATE_NAME.PLO4) return handleButton(handlerCancelSub, 'Cancel PLO4+PLO5', bgColor);
      if (type === RATE_NAME.PLO5) return handleButton(handlerCancelSub, 'Cancel PLO4+PLO5', bgColor);
      if (type === RATE_NAME.PLO4PLO5) return handleButton(handlerCancelSub, 'Cancel PLO4+PLO5', bgColor);
      if (type === RATE_NAME.PLO4_YEAR) return buttonUpgrade({ title: 'Upgrade subscription', id: PRICE_KEY.PLO4_YEAR, to: 'PLO4 (€405/year)' }, bgColor);
      if (type === RATE_NAME.PLO5_YEAR) return buttonUpgrade({ title: 'Upgrade subscription', id: PRICE_KEY.PLO5_YEAR, to: 'PLO5 (€405/year)' }, bgColor);
      if (type === RATE_NAME.PLO4PLO5_YEAR) return buttonUpgrade({ title: 'Upgrade subscription', id: PRICE_KEY.PLO4PLO5_YEAR, to: 'PLO4+PLO5 (€660/year)' }, bgColor);
    }

    if (isPlo4YearActive && isStripe) {
      if (type === RATE_NAME.PLO4) return handleButton(handlerCancelSub, 'Cancel PLO4 Annual', bgColor);
      if (type === RATE_NAME.PLO5) return handleButton(handlerCancelSub, 'Cancel PLO4 Annual', bgColor);
      if (type === RATE_NAME.PLO4PLO5) return handleButton(handlerCancelSub, 'Cancel PLO4 Annual', bgColor);
      if (type === RATE_NAME.PLO4_YEAR) return handleButton(handlerCancelSub, 'Cancel PLO4 Annual', bgColor);
      if (type === RATE_NAME.PLO5_YEAR) return handleButton(handlerCancelSub, 'Cancel PLO4 Annual', bgColor);
      if (type === RATE_NAME.PLO4PLO5_YEAR) return buttonUpgrade({ title: 'Upgrade subscription', id: PRICE_KEY.PLO4PLO5_YEAR, to: 'PLO4+PLO5 (€660/year)' }, bgColor);
    }

    if (isPlo5YearActive && isStripe) {
      if (type === RATE_NAME.PLO4) return handleButton(handlerCancelSub, 'Cancel PLO5 Annual', bgColor);
      if (type === RATE_NAME.PLO5) return handleButton(handlerCancelSub, 'Cancel PLO5 Annual', bgColor);
      if (type === RATE_NAME.PLO4PLO5) return handleButton(handlerCancelSub, 'Cancel PLO5 Annual', bgColor);
      if (type === RATE_NAME.PLO4_YEAR) return handleButton(handlerCancelSub, 'Cancel PLO5 Annual', bgColor);
      if (type === RATE_NAME.PLO5_YEAR) return handleButton(handlerCancelSub, 'Cancel PLO5 Annual', bgColor);
      if (type === RATE_NAME.PLO4PLO5_YEAR) return buttonUpgrade({ title: 'Upgrade subscription', id: PRICE_KEY.PLO4PLO5_YEAR, to: 'PLO4+PLO5 (€660/year)' }, bgColor);
    }

    if (isPlo4Plo5YearActive && isStripe) {
      if (type === RATE_NAME.PLO4) return handleButton(handlerCancelSub, 'Cancel PLO4+PLO5 Annual', bgColor);
      if (type === RATE_NAME.PLO5) return handleButton(handlerCancelSub, 'Cancel PLO4+PLO5 Annual', bgColor);
      if (type === RATE_NAME.PLO4PLO5) return handleButton(handlerCancelSub, 'Cancel PLO4+PLO5 Annual', bgColor);
      if (type === RATE_NAME.PLO4_YEAR) return handleButton(handlerCancelSub, 'Cancel PLO4+PLO5 Annual', bgColor);
      if (type === RATE_NAME.PLO5_YEAR) return handleButton(handlerCancelSub, 'Cancel PLO4+PLO5 Annual', bgColor);
      if (type === RATE_NAME.PLO4PLO5_YEAR) return handleButton(handlerCancelSub, 'Cancel PLO4+PLO5 Annual', bgColor);
    }

    if (isPlo4 && isStripe && isPausedSub) {
      if (type === RATE_NAME.PLO4) return handleButton(handlerResumeButton, 'Resume', bgColor);
      if (type === RATE_NAME.PLO5) return disabledButton(bgColor);
      if (type === RATE_NAME.PLO4PLO5) return buttonUpgrade({ title: 'Upgrade subscription', id: PRICE_KEY.PLO4PLO5, to: 'PLO4+PLO5 (€70/month)' }, bgColor);
      if (type === RATE_NAME.PLO4_YEAR) return buttonUpgrade({ title: 'Upgrade subscription', id: PRICE_KEY.PLO4_YEAR, to: 'PLO4 (€405/year)' }, bgColor);
      if (type === RATE_NAME.PLO5_YEAR) return buttonUpgrade({ title: 'Upgrade subscription', id: PRICE_KEY.PLO5_YEAR, to: 'PLO5 (€405/year)' }, bgColor);
      if (type === RATE_NAME.PLO4PLO5_YEAR) return buttonUpgrade({ title: 'Upgrade subscription', id: PRICE_KEY.PLO4PLO5_YEAR, to: 'PLO4+PLO5 (€660/year)' }, bgColor);
    }

    if (isPlo5 && isStripe && isPausedSub) {
      if (type === RATE_NAME.PLO4) return disabledButton(bgColor);
      if (type === RATE_NAME.PLO5) return handleButton(handlerResumeButton, 'Resume', bgColor);
      if (type === RATE_NAME.PLO4PLO5) return buttonUpgrade({ title: 'Upgrade subscription', id: PRICE_KEY.PLO4PLO5, to: 'PLO4+PLO5 (€70/month)' }, bgColor);
      if (type === RATE_NAME.PLO4_YEAR) return buttonUpgrade({ title: 'Upgrade subscription', id: PRICE_KEY.PLO4_YEAR, to: 'PLO4 (€405/year)' }, bgColor);
      if (type === RATE_NAME.PLO5_YEAR) return buttonUpgrade({ title: 'Upgrade subscription', id: PRICE_KEY.PLO5_YEAR, to: 'PLO5 (€405/year)' }, bgColor);
      if (type === RATE_NAME.PLO4PLO5_YEAR) return buttonUpgrade({ title: 'Upgrade subscription', id: PRICE_KEY.PLO4PLO5_YEAR, to: 'PLO4+PLO5 (€660/year)' }, bgColor);
    }

    if (isPlo4Plo5 && isStripe && isPausedSub) {
      if (type === RATE_NAME.PLO4) return disabledButton(bgColor);
      if (type === RATE_NAME.PLO5) return disabledButton(bgColor);
      if (type === RATE_NAME.PLO4PLO5) return handleButton(handlerResumeButton, 'Resume', bgColor);
      if (type === RATE_NAME.PLO4_YEAR) return buttonUpgrade({ title: 'Upgrade subscription', id: PRICE_KEY.PLO4_YEAR, to: 'PLO4 (€405/year)' }, bgColor);
      if (type === RATE_NAME.PLO5_YEAR) return buttonUpgrade({ title: 'Upgrade subscription', id: PRICE_KEY.PLO5_YEAR, to: 'PLO5 (€405/year)' }, bgColor);
      if (type === RATE_NAME.PLO4PLO5_YEAR) return buttonUpgrade({ title: 'Upgrade subscription', id: PRICE_KEY.PLO4PLO5_YEAR, to: 'PLO4+PLO5 (€660/year)' }, bgColor);
    }

    if (isPlo4Year && isStripe && isPausedSub) {
      if (type === RATE_NAME.PLO4) return disabledButton(bgColor);
      if (type === RATE_NAME.PLO5) return disabledButton(bgColor);
      if (type === RATE_NAME.PLO4PLO5) return disabledButton(bgColor);
      if (type === RATE_NAME.PLO4_YEAR) return handleButton(handlerResumeButton, 'Resume', bgColor);
      if (type === RATE_NAME.PLO5_YEAR) return disabledButton(bgColor);
      if (type === RATE_NAME.PLO4PLO5_YEAR) return buttonUpgrade({ title: 'Upgrade subscription', id: PRICE_KEY.PLO4PLO5_YEAR, to: 'PLO4+PLO5 (€660/year)' }, bgColor);
    }

    if (isPlo5Year && isStripe && isPausedSub) {
      if (type === RATE_NAME.PLO4) return disabledButton(bgColor);
      if (type === RATE_NAME.PLO5) return disabledButton(bgColor);
      if (type === RATE_NAME.PLO4PLO5) return disabledButton(bgColor);
      if (type === RATE_NAME.PLO4_YEAR) return disabledButton(bgColor);
      if (type === RATE_NAME.PLO5_YEAR) return handleButton(handlerResumeButton, 'Resume', bgColor);
      if (type === RATE_NAME.PLO4PLO5_YEAR) return buttonUpgrade({ title: 'Upgrade subscription', id: PRICE_KEY.PLO4PLO5_YEAR, to: 'PLO4+PLO5 (€660/year)' }, bgColor);
    }

    if (isPlo4Plo5Year && isStripe && isPausedSub) {
      if (type === RATE_NAME.PLO4PLO5_YEAR) return handleButton(handlerResumeButton, 'Resume', bgColor);
      return disabledButton(bgColor);
    }

    if (isAdvancedActive && isStripe) {
      if (type === RATE_NAME.BASIC) return handleButton(handlerCancelSub, 'Cancel advanced', bgColor);
      if (type === RATE_NAME.ADVANCED) return handleButton(handlerCancelSub, 'Cancel advanced', bgColor);
      if (type === RATE_NAME.BASIC_YEAR) return buttonUpgrade({ title: 'Upgrade subscription', id: PRICE_KEY.BASIC_YEAR, to: 'Basic annual (€315/year)' }, bgColor);
      if (type === RATE_NAME.ADVANCED_YEAR) return buttonUpgrade({ title: 'Upgrade subscription', id: PRICE_KEY.ADVANCED_YEAR, to: 'Advanced annual (€405/year)' }, bgColor);
    }

    if (isBasicAnnualActive && isStripe) {
      if (type === RATE_NAME.BASIC_YEAR) return handleButton(handlerCancelSub, 'Cancel basic', bgColor);
      if (type === RATE_NAME.ADVANCED_YEAR) return buttonUpgrade({ title: 'Upgrade subscription', id: PRICE_KEY.ADVANCED_YEAR, to: 'Advanced annual (€405/year)' }, bgColor);
    }

    if (isAdvancedAnnualActive && isStripe) {
      if (type === RATE_NAME.BASIC_YEAR) return handleButton(handlerCancelSub, 'Cancel advanced', bgColor);
      if (type === RATE_NAME.ADVANCED_YEAR) return handleButton(handlerCancelSub, 'Cancel advanced', bgColor);
    }

    if (isPendingSub) {
      return disabledButton(bgColor, 'Pending subscription');
    }

    return isLoggedIn ? button : null;
  };

  const pricingData = useMemo(() => ([
    // {
    //   backGround: 'linear-gradient(205.37deg, #4B5F68 -0.05%, #364852 99.95%)',
    //   title: 'ADVANCED',
    // eslint-disable-next-line max-len
    //   text: 'Access to the Preflop and Training will help you better understand ranges and consolidate this knowledge. ',
    //   price: '€15',
    //   features: ['Preflop', 'Compare ranges', 'Training', 'EV', 'Discord', 'Results'],
    //   button: isLoggedIn ? buttonTrial : null,
    //   type: 'PER WEEK',
    //   subtext: 'You can try all the advanced subscription features only once.',
    // },
    {
      title: 'PLO4',
      text: '',
      price: '€45.00',
      backGround: 'linear-gradient(180deg, #010101, #296183)',
      borderTopColor: '#17C7FF',
      features: ['Training for PLO4', 'Preflop for PLO4', 'Compare ranges', 'EV', 'Discord'],
      button: handleButtonType(
        handleButton(
          () => handlerButtonSubscribe(routes.app.payments.toPayments(RATE_NAME.PLO4)), 'Subscribe now', 'linear-gradient(180deg, #17C7FF 0%, #0B95E1 100%)',
        ),
        RATE_NAME.PLO4,
        'linear-gradient(180deg, #17C7FF 0%, #0B95E1 100%)',
      ),
      type: 'per month',
      subtext: '',
      id: '1',
    },
    {
      title: 'PLO5',
      text: '',
      price: '€45.00',
      backGround: 'linear-gradient(180deg, #010101, #832929)',
      borderTopColor: '#EC173B',
      features: ['Training for PLO5', 'Preflop for PLO5', 'Compare ranges', 'EV', 'Discord'],
      button: handleButtonType(
        handleButton(
          () => handlerButtonSubscribe(routes.app.payments.toPayments(RATE_NAME.PLO5)), 'Subscribe now', 'linear-gradient(0deg, #EC173B 0%, #FF1738 100%)',
        ),
        RATE_NAME.PLO5,
        'linear-gradient(0deg, #EC173B 0%, #FF1738 100%)',
      ),
      type: 'per month',
      subtext: '',
      id: '2',
    },
    {
      title: 'PLO4+PLO5',
      text: '',
      price: '€70.00',
      backGround: '#1E252E',
      features: ['Training (PLO4+PLO5)', 'Preflop (PLO4+PLO5)', 'Compare ranges', 'EV', 'Discord'],
      button: handleButtonType(
        handleButton(
          () => handlerButtonSubscribe(routes.app.payments.toPayments(RATE_NAME.PLO4PLO5)), 'Subscribe now', 'linear-gradient(90deg, #17C7FF 0%, #FF1738 100%)',
        ),
        RATE_NAME.PLO4PLO5,
        'linear-gradient(90deg, #17C7FF 0%, #FF1738 100%)',
      ),
      type: 'per month',
      subtext: '',
      isCombination: true,
      id: '3',
    },
  ]), [user]);

  const pricingYear = useMemo(() => ([
    {
      title: 'PLO4',
      text: '',
      price: '€33.75',
      backGround: 'linear-gradient(180deg, #010101, #296183)',
      borderTopColor: '#17C7FF',
      features: ['Training for PLO4', 'Preflop for PLO4', 'Compare ranges', 'EV', 'Discord'],
      button: handleButtonType(
        handleButton(
          () => handlerButtonSubscribe(routes.app.payments.toPayments(RATE_NAME.PLO4_YEAR)), 'Subscribe now', 'linear-gradient(180deg, #17C7FF 0%, #0B95E1 100%)',
        ),
        RATE_NAME.PLO4_YEAR,
        'linear-gradient(180deg, #17C7FF 0%, #0B95E1 100%)',
      ),
      isYear: true,
      oldPrice: '€45.00',
      percentage: '-25%',
      percentageColor: '#0B95E1',
      type: 'per month',
      id: '4',
    },
    {
      title: 'PLO5',
      text: '',
      price: '€33.75',
      backGround: 'linear-gradient(180deg, #010101, #832929)',
      borderTopColor: '#EC173B',
      features: ['Training for PLO5', 'Preflop for PLO5', 'Compare ranges', 'EV', 'Discord'],
      button: handleButtonType(
        handleButton(
          () => handlerButtonSubscribe(routes.app.payments.toPayments(RATE_NAME.PLO5_YEAR)), 'Subscribe now', 'linear-gradient(0deg, #EC173B 0%, #FF1738 100%)',
        ),
        RATE_NAME.PLO5_YEAR,
        'linear-gradient(0deg, #EC173B 0%, #FF1738 100%)',
      ),
      isYear: true,
      oldPrice: '€45.00',
      percentage: '-25%',
      percentageColor: '#FF1738',
      type: 'per month',
      id: '5',
    },
    {
      title: 'PLO4+PLO5',
      text: '',
      price: '€55.00',
      oldPrice: '€70.00',
      backGround: '#1E252E',
      features: ['Training (PLO4+PLO5)', 'Preflop (PLO4+PLO5)', 'Compare ranges', 'EV', 'Discord'],
      button: handleButtonType(
        handleButton(
          () => handlerButtonSubscribe(routes.app.payments.toPayments(RATE_NAME.PLO4PLO5_YEAR)), 'Subscribe now', 'linear-gradient(90deg, #17C7FF 0%, #FF1738 100%)',
        ),
        RATE_NAME.PLO4PLO5_YEAR,
        'linear-gradient(90deg, #17C7FF 0%, #FF1738 100%)',
      ),
      type: 'per month',
      isYear: true,
      subtext: '',
      percentage: '-39%',
      percentageColor: '#121719',
      isCombination: true,
      id: '6',
    },
  ]), [user]);

  const selectedTypePricing = useMemo(() => (isYear ? pricingData : pricingYear), [isYear, user]);

  const pricing = selectedTypePricing?.filter((ev) => {
    // maybe deprecate
    if (isTest && ev.price === '€15') return null;
    if (isBasicActive && ev.price === '€15') return null;
    if (isAdvancedActive && ev.price === '€15') return null;
    if (isBasicAnnualActive && (ev.price === '€15' || ev.price === '€35' || ev.price === '€45')) return null;
    if (isAdvancedAnnualActive && (ev.price === '€15' || ev.price === '€35' || ev.price === '€45')) return null;
    return ev;
  });

  return { pricing };
};
export default usePricingData;
